import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { useState } from "react";
import { Check, ArrowRight, Sparkles, Star } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { useCurrency } from "@/lib/ecom/currency";
import {
  PLANS,
  getPlanMonthly,
  formatPlanPrice,
  defaultPlanPriceText,
} from "@/components/marketing/pricing-data";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing — banana.cards" },
      { name: "description", content: "Simple, transparent pricing for individuals and teams. Start free and scale with AI when you need it." },
      { property: "og:title", content: "banana.cards Pricing" },
      { property: "og:description", content: "Free forever for individuals. Pro AI for serious networkers. Teams plan with per-seat billing. Enterprise on request." },
      { property: "og:url", content: "https://banana.cards/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Is the free plan really free forever?", acceptedAnswer: { "@type": "Answer", text: "Yes. You can use banana.cards as a digital business card and basic CRM for free, forever — no credit card required." } },
            { "@type": "Question", name: "Do I need to buy an NFC card?", acceptedAnswer: { "@type": "Answer", text: "No — your digital card works via QR, link, email signature or Apple/Google Wallet. NFC cards are an optional premium add-on." } },
            { "@type": "Question", name: "Can I switch plans later?", acceptedAnswer: { "@type": "Answer", text: "Absolutely. Upgrade, downgrade or pause anytime from your workspace settings." } },
            { "@type": "Question", name: "How does AI usage work?", acceptedAnswer: { "@type": "Answer", text: "Pro AI includes 200 AI emails, 100 enhancements and 50 card scans per month. Teams get higher limits, Enterprise is unlimited." } },
            { "@type": "Question", name: "Do you offer student or non-profit discounts?", acceptedAnswer: { "@type": "Answer", text: "Yes — get in touch with our team and we'll set you up." } },
            { "@type": "Question", name: "Is my data secure?", acceptedAnswer: { "@type": "Answer", text: "All data is encrypted in transit and at rest. Teams and Enterprise plans support SSO and signed DPAs." } },
          ],
        }),
      },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const [interval, setInterval] = useState<"month" | "year">("year");
  const [seats, setSeats] = useState(3);
  const { country } = useCurrency();

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-4 pt-20 pb-12 text-center md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Pricing</p>
        <h1 className="mx-auto mt-3 max-w-3xl font-display text-3xl leading-[1.1] md:text-5xl">
          Start free. Grow when <em className="italic">you're ready.</em>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
          No credit card required. Cancel anytime. Prices auto-update for your region.
        </p>

        {/* Billing toggle */}
        <div className="mt-10 inline-flex items-center gap-2 rounded-full bg-muted p-1">
          <button
            type="button"
            onClick={() => setInterval("month")}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
              interval === "month" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval("year")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
              interval === "year" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Yearly
            <span className="rounded-full bg-brand-lime px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-brand-lime-foreground">
              Save with Yearly
            </span>
          </button>
        </div>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const isFree = p.key === "free";
            const isEnterprise = p.key === "enterprise";
            const isPaid = p.key === "pro_ai" || p.key === "teams";
            const paidKey = (p.key === "pro_ai" || p.key === "teams") ? p.key : null;
            const priceText = paidKey
              ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, interval))
              : defaultPlanPriceText("pro_ai", interval);
            const firstYearPriceText = paidKey
              ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, interval) / 2)
              : "";
            const seatTotalText = paidKey
              ? formatPlanPrice(
                  country,
                  (interval === "year"
                    ? getPlanMonthly(paidKey, country.code, "year") / 2
                    : getPlanMonthly(paidKey, country.code, interval)) * seats,
                )
              : "";
            void isPaid;

            return (
              <div
                key={p.key}
                className={`relative flex flex-col rounded-2xl bg-muted p-6 transition ${
                  p.popular
                    ? "ring-2 ring-foreground"
                    : ""
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 left-6 rounded-full bg-foreground px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider text-background">
                    Most popular
                  </span>
                )}

                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-card">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="text-lg font-bold">{p.name}</h3>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{p.tagline}</p>

                {/* Price */}
                <div className="mt-6 min-h-[80px]">
                  {isFree ? (
                    <>
                      <p className="font-display text-5xl leading-none">Free</p>
                      <p className="mt-1 text-xs text-muted-foreground">Forever, for individuals.</p>
                    </>
                  ) : isEnterprise ? (
                    <>
                      <p className="font-display text-4xl leading-none">Custom</p>
                      <p className="mt-2 text-xs text-muted-foreground">Tailored pricing & SLAs.</p>
                    </>
                  ) : (
                    <>
                      {interval === "year" ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-display text-lg leading-none tracking-tight text-muted-foreground line-through sm:text-xl">
                            {priceText}
                          </span>
                          <div className="flex flex-wrap items-baseline gap-x-1">
                            <span className="font-display text-4xl leading-none tracking-tight sm:text-5xl">
                              {firstYearPriceText}
                            </span>
                            <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                              /{p.perSeat ? "seat/" : ""}mo
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                          <span className="font-display text-4xl leading-none tracking-tight sm:text-5xl">
                            {priceText}
                          </span>
                          <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                            /{p.perSeat ? "seat/" : ""}mo
                          </span>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {interval === "year" ? "Billed yearly" : "Billed monthly"}
                      </p>
                      {interval === "year" ? (
                        <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-brand-lime/30 px-2 py-0.5 text-[10px] font-bold text-brand-lime-foreground">
                          50% OFF first year
                        </p>
                      ) : (
                        <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-brand-lime-foreground">
                          Get 50% off when you select yearly
                        </p>
                      )}
                      {p.perSeat && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Seats</span>
                          <div className="flex items-center overflow-hidden rounded-full bg-card ring-1 ring-foreground/10">
                            <button aria-label="Decrease seats" onClick={() => setSeats((s) => Math.max(3, s - 1))} className="h-7 w-7 text-sm font-bold hover:bg-card">–</button>
                            <span className="w-8 border-x border-foreground/10 text-center text-xs font-bold">{seats}</span>
                            <button aria-label="Increase seats" onClick={() => setSeats((s) => Math.min(500, s + 1))} className="h-7 w-7 text-sm font-bold hover:bg-card">+</button>
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            ≈ {seatTotalText}/mo
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* CTA */}
                <CtaLink
                  to={isEnterprise ? "/contact" : "/signup"}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                    p.popular
                      ? "bg-brand-lime text-brand-lime-foreground hover:scale-[1.02]"
                      : "bg-foreground text-background hover:opacity-90"
                  }`}
                >
                  {p.cta}
                  <ArrowRight className="h-4 w-4" />
                </CtaLink>

                {/* Features */}
                <ul className="mt-6 space-y-2.5 border-t border-foreground/5 pt-6">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${i === 0 && !f.startsWith("Everything") ? "" : ""}`}>
                      {f.startsWith("Everything in") ? (
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{f}</span>
                      ) : (
                        <>
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                          <span>{f}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          </div>
        </div>
      </section>

      {/* Compare strip */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="rounded-3xl bg-card p-8 md:p-12">
          <div className="grid items-center gap-8 md:grid-cols-[1.4fr_2fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">For your whole team</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Built for <em className="italic">teams that meet people.</em>
              </h2>
              <p className="mt-4 text-sm text-muted-foreground">
                Roll out branded cards, shared CRM and AI follow-ups across your entire org. Discounts for 25+ seats.
              </p>
              <div className="mt-6 flex gap-2">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background hover:opacity-90">
                  Talk to sales <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {[
                "Team-branded card templates",
                "Shared contacts & pipelines",
                "Admin permissions & SSO",
                "Bulk NFC card ordering",
                "Custom domains & vanity links",
                "Priority phone & chat support",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 rounded-xl bg-muted p-3 text-sm">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="font-semibold">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background">
        <div className="mx-auto max-w-3xl px-4 py-24 md:px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">FAQ</p>
        <h2 className="mt-2 text-center font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Questions, <em className="italic">answered.</em>
        </h2>
        <div className="mt-10 space-y-3">
          {[
            { q: "Is the free plan really free forever?", a: "Yes. You can use banana.cards as a digital business card and basic CRM for free, forever — no credit card required." },
            { q: "Do I need to buy an NFC card?", a: "No — your digital card works via QR, link, email signature or Apple/Google Wallet. NFC cards are an optional premium add-on." },
            { q: "Can I switch plans later?", a: "Absolutely. Upgrade, downgrade or pause anytime from your workspace settings." },
            { q: "How does AI usage work?", a: "Pro AI includes 200 AI emails, 100 enhancements and 50 card scans per month. Teams get higher limits, Enterprise is unlimited." },
            { q: "Do you offer student or non-profit discounts?", a: "Yes — get in touch with our team and we'll set you up." },
            { q: "Is my data secure?", a: "All data is encrypted in transit and at rest. Teams and Enterprise plans support SSO and signed DPAs." },
          ].map((f, i) => (
            <details key={i} className="group rounded-2xl bg-muted p-5">
              <summary className="flex cursor-pointer items-center justify-between text-sm font-bold">
                {f.q}
                <span className="ml-3 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-card transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
          <div className="mx-auto inline-flex items-center gap-1 rounded-full bg-background/10 px-3 py-1 text-xs font-bold text-brand-lime ring-1 ring-background/10">
            <Star className="h-3 w-3 fill-current" /> 4.9 / 5 across 12k+ reviews
          </div>
          <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            Try banana.cards <em className="italic text-brand-lime">free</em> for 14 days.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              Start free
            </CtaLink>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              Talk to sales
            </Link>
          </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
